import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { notifyTaskAssigned } from '@/lib/notifications';
import { fireQuickBurst, fireSuccessConfetti } from '@/lib/confetti';
import { useOrganization } from '@/hooks/useOrganization';
import { trackTaskCreated, trackTaskCompleted } from '@/lib/analytics';

export interface Task {
  id: string;
  title: string;
  description: string | null;
  status: 'pending' | 'in_progress' | 'review' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to: string | null;
  created_by: string | null;
  due_date: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  deleted_at: string | null;
}

export interface CreateTaskData {
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: string;
  due_date?: string;
  tags?: string[];
}

export function useTasks(includeArchived: boolean = false) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [deletedTasks, setDeletedTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const { toast } = useToast();
  const { organization } = useOrganization();

  const fetchTasks = async () => {
    try {
      // Fetch active/archived tasks
      let query = supabase
        .from('tasks')
        .select('*')
        .is('deleted_at', null);

      if (!includeArchived) {
        query = query.is('archived_at', null);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      setTasks(data as Task[] || []);

      // Fetch deleted tasks (trash)
      const { data: trashData, error: trashError } = await supabase
        .from('tasks')
        .select('*')
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (trashError) throw trashError;
      setDeletedTasks(trashData as Task[] || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch tasks.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const logActivity = async (action: string, entityType: string, entityId: string, details: Record<string, unknown>) => {
    try {
      await supabase.rpc('log_activity', {
        p_action: action,
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_details: JSON.parse(JSON.stringify(details))
      });
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };

  const createTask = async (data: CreateTaskData) => {
    if (!data.title?.trim()) {
      toast({
        title: 'Error',
        description: 'Task title is required.',
        variant: 'destructive'
      });
      return false;
    }

    try {
      const { data: userData } = await supabase.auth.getUser();
      const { data: newTask, error } = await supabase.from('tasks').insert({
        ...data,
        title: data.title.trim(),
        description: data.description?.trim() || null,
        created_by: userData.user?.id,
        organization_id: organization?.id || null
      }).select().single();

      if (error) throw error;
      
      // Log activity
      if (newTask) {
        await logActivity('task_created', 'task', newTask.id, { title: data.title });
        trackTaskCreated(data.priority);
      }
      
      // Send notification if task is assigned
      // Note: We need the real email for notifications, so we use the base table here
      // This is a server-side operation, not exposed to the client UI
      if (data.assigned_to && newTask) {
        const { data: assignee } = await supabase
          .from('team_members')
          .select('email, name')
          .eq('id', data.assigned_to)
          .single();
        
        // Use secure profile function for creator info
        const { data: creatorProfiles } = await supabase
          .rpc('get_profile_safe', { p_user_id: userData.user?.id || '' });
        const creator = creatorProfiles?.[0];

        if (assignee) {
          notifyTaskAssigned(
            assignee.email,
            assignee.name,
            data.title,
            data.description || null,
            creator?.full_name || null,
            data.due_date || null
          ).catch(console.error);
        }
      }
      
      fireQuickBurst(0.5, 0.5);
      toast({ title: '✨ Task Created', description: 'Your task has been created successfully.' });
      await fetchTasks();
      return true;
    } catch (error) {
      console.error('Error creating task:', error);
      toast({
        title: 'Error',
        description: 'Failed to create task.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const updateTask = async (id: string, updates: Partial<Task>) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      
      // Log activity
      await logActivity('task_updated', 'task', id, { updates });
      
      if (updates.status === 'completed') {
        trackTaskCompleted(id);
        fireSuccessConfetti();
        toast({ title: '🎉 Task Completed!', description: 'Great job! Keep up the momentum.' });
      } else {
        toast({ title: 'Updated', description: 'Task updated successfully.' });
      }
      await fetchTasks();
      return true;
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: 'Error',
        description: 'Failed to update task.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const archiveTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      await logActivity('task_archived', 'task', id, {});
      toast({ title: 'Archived', description: 'Task has been archived.' });
      await fetchTasks();
      return true;
    } catch (error) {
      console.error('Error archiving task:', error);
      toast({
        title: 'Error',
        description: 'Failed to archive task.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const restoreTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ archived_at: null, deleted_at: null })
        .eq('id', id);

      if (error) throw error;
      
      await logActivity('task_restored', 'task', id, {});
      toast({ title: 'Restored', description: 'Task has been restored.' });
      await fetchTasks();
      return true;
    } catch (error) {
      console.error('Error restoring task:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore task.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const softDeleteTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      await logActivity('task_deleted', 'task', id, {});
      toast({ title: 'Deleted', description: 'Task moved to trash. It will be permanently deleted after 30 days.' });
      await fetchTasks();
      return true;
    } catch (error) {
      console.error('Error soft deleting task:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete task.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const permanentlyDeleteTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({ title: 'Permanently Deleted', description: 'Task has been permanently removed.' });
      await fetchTasks();
      return true;
    } catch (error) {
      console.error('Error permanently deleting task:', error);
      toast({
        title: 'Error',
        description: 'Failed to permanently delete task.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const deleteTask = async (id: string) => {
    return softDeleteTask(id);
  };

  // Bulk operations with progress
  const bulkArchive = useCallback(async (ids: string[]) => {
    setBulkProgress({ current: 0, total: ids.length });
    let successCount = 0;
    
    for (let i = 0; i < ids.length; i++) {
      const result = await archiveTask(ids[i]);
      if (result) successCount++;
      setBulkProgress({ current: i + 1, total: ids.length });
    }
    
    setBulkProgress(null);
    return successCount;
  }, []);

  const bulkRestore = useCallback(async (ids: string[]) => {
    setBulkProgress({ current: 0, total: ids.length });
    let successCount = 0;
    
    for (let i = 0; i < ids.length; i++) {
      const result = await restoreTask(ids[i]);
      if (result) successCount++;
      setBulkProgress({ current: i + 1, total: ids.length });
    }
    
    setBulkProgress(null);
    return successCount;
  }, []);

  const bulkDelete = useCallback(async (ids: string[]) => {
    setBulkProgress({ current: 0, total: ids.length });
    let successCount = 0;
    
    for (let i = 0; i < ids.length; i++) {
      const result = await softDeleteTask(ids[i]);
      if (result) successCount++;
      setBulkProgress({ current: i + 1, total: ids.length });
    }
    
    setBulkProgress(null);
    return successCount;
  }, []);

  const bulkPermanentDelete = useCallback(async (ids: string[]) => {
    setBulkProgress({ current: 0, total: ids.length });
    let successCount = 0;
    
    for (let i = 0; i < ids.length; i++) {
      const result = await permanentlyDeleteTask(ids[i]);
      if (result) successCount++;
      setBulkProgress({ current: i + 1, total: ids.length });
    }
    
    setBulkProgress(null);
    return successCount;
  }, []);

  // Calculate days until permanent deletion
  const getDaysUntilPermanentDelete = (deletedAt: string) => {
    const deleteDate = new Date(deletedAt);
    const now = new Date();
    const diffTime = 30 * 24 * 60 * 60 * 1000 - (now.getTime() - deleteDate.getTime());
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => {
    fetchTasks();

    const channel = supabase
      .channel('tasks-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks' },
        () => {
          fetchTasks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [includeArchived]);

  const activeTasks = tasks.filter(t => !t.archived_at && !t.deleted_at);
  const archivedTasks = tasks.filter(t => t.archived_at && !t.deleted_at);

  return {
    tasks: activeTasks,
    archivedTasks,
    deletedTasks,
    allTasks: tasks,
    loading,
    bulkProgress,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    archiveTask,
    restoreTask,
    softDeleteTask,
    permanentlyDeleteTask,
    bulkArchive,
    bulkRestore,
    bulkDelete,
    bulkPermanentDelete,
    getDaysUntilPermanentDelete
  };
}
